/**
 * STARSEED-CAPMARKETS-194 — enter a partner balance without an engineer.
 *
 * `POST /partner-statements/import` has been live since 2026-08-03 and no web
 * surface called it. So every balance change needed an engineer with production
 * credentials, and on 2026-08-24 it needed one to first *rewrite the driver*,
 * because the 2026-08-21 one was never committed. Two members' figures were
 * updated that day; both were already mapped, and nothing about it was blocked
 * on the partner. That is the cost this page removes.
 *
 * Three decisions shape it:
 *
 * 1. **It takes what the card has and derives what it does not.** A partner
 *    console card carries `Balance` and `Open Trades Profit` and no equity
 *    column — CAPMARKETS-179's root cause. Asking an operator to compute equity
 *    is asking them to redo arithmetic that has already gone wrong once.
 * 2. **Nothing is written until a dry run has been shown.** The server projects
 *    the member's before/after totals with the same `sum_latest_per_account`
 *    the dashboard uses, so the operator sees the figure the MEMBER will see
 *    before committing to it. The preview cannot disagree with the write
 *    because neither computes the total itself.
 * 3. **A blank field is absent, never zero.** `buildCsv` emits an empty cell
 *    and the server's validator records `None`. A fabricated zero here would be
 *    an immutable ledger row, which is worse than the render bug CAPMARKETS-094
 *    fixed.
 *
 * It posts a CSV to the existing route rather than to a JSON sibling, so
 * validation, `(partner, ref, as_of)` idempotency and member resolution stay in
 * exactly one implementation (AC7).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { AlertTriangle, Calculator, Eye, Plus, Save, Trash2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { describeMoney } from "../lib/partnerAccountQueue";
import StatementScreenshotImport from "../components/StatementScreenshotImport";
import {
  DISPLAY_NAME_REF_COLUMN,
  applyScreenshot,
  buildCsv,
  deriveEquity,
  describeProjection,
  duplicateAccountRefs,
  parseMoneyInput,
  questionFor,
  removeRow,
  resolvePendingApply,
  screenshotDisagreements,
  screenshotReadings,
  validateRow,
} from "../lib/balanceEntry";

const CONFIDENCE_OPTIONS = [
  { value: "transcribed", label: "Transcribed — read off a screenshot or console card" },
  { value: "exported", label: "Exported — taken from a file the partner produced" },
];

const blankRow = () => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  accountRef: "",
  accountLabel: "",
  balance: "",
  openTradesProfit: "",
});

const errorText = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => (typeof d?.msg === "string" ? d.msg : ""))
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  return fallback;
};

const Money = ({ value }) => {
  const d = describeMoney(value);
  return d.known ? (
    <span className="font-mono">{d.text}</span>
  ) : (
    <span className="text-foreground-soft italic">Unknown</span>
  );
};

/** A state updater that changes only the editor's rows. */
const withRows = (change) => (editor) => ({ ...editor, rows: change(editor.rows) });

/**
 * The row editor with nothing in it. `pending` is a fill-in waiting on the
 * operator's answer, `held` the members whose fill-in was held back while an
 * answer was outstanding and has not been filled in since, `removed` the
 * screenshot blocks whose rows the operator
 * removed, `skipped` the accounts the last fill-in did not add back because of
 * that, and `mixed` the pairs of members the operator chose to keep side by side
 * (`lib/balanceEntry.js`, CAPMARKETS-239).
 */
const freshEditor = () => ({
  rows: [blankRow()], pending: null, held: [], removed: {}, skipped: [], mixed: [],
});

const refOf = (row) => String(row?.accountRef ?? "").trim();

/** How the member question names one of the rows it would replace. */
const namedRow = (row) => refOf(row) || "a row with no account";

/** What a screenshot read, as the operator would type it. */
const screenshotFigure = (value) => {
  const n = parseMoneyInput(value);
  return n === null || Number.isNaN(n) ? "no figure" : describeMoney(n).text;
};

const AdminBalanceEntryPage = () => {
  // The rows, and a screenshot fill-in waiting on the operator, are ONE state
  // value: whether a fill-in crosses members is decided against the same rows
  // it would merge into (CAPMARKETS-239).
  const [editor, setEditor] = useState(freshEditor);
  const { rows, skipped } = editor;
  const held = editor.held || [];
  // Worked out from the rows as they are now, every render: what the question
  // lists is exactly what Replace would remove (CAPMARKETS-239, review of 53119886).
  const question = questionFor(editor);
  // A write ends a statement. Remounting the importer drops its proposals, so a
  // block written or deliberately removed cannot come back on the next Fill in.
  const [importerKey, setImporterKey] = useState(0);
  const [asOf, setAsOf] = useState("");
  const [confidence, setConfidence] = useState("transcribed");
  // What the operator typed in the Account field. A console display name is a
  // materially weaker identity than a login — it is editable, so a rename forks
  // the account's history — and the row must record which it is rather than
  // claim the stronger one by default (CAPMARKETS-133).
  const [keyedBy, setKeyedBy] = useState("login");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState(null);
  // CAPMARKETS-230. Only members with at least one MAPPED account are offered:
  // the ranker draws candidates from mappings, so a member without one can only
  // ever be refused. Offering them would be a dead end dressed as a choice.
  const [members, setMembers] = useState([]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [u, m] = await Promise.all([
          api.get("/users", { params: { limit: 500 } }),
          api.get("/admin/partner-accounts/mappings"),
        ]);
        if (!live) return;
        const mapped = new Set(
          (m.data?.mappings || []).map((x) => x.member_id).filter(Boolean),
        );
        setMembers(
          (Array.isArray(u.data) ? u.data : [])
            .filter((x) => x && mapped.has(x.id))
            .map((x) => ({ id: x.id, name: x.full_name || x.email || x.id }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      } catch {
        // A failed member list degrades to manual entry, which is the state
        // this page shipped in. Not surfaced as an error because nothing the
        // operator was doing has failed.
        if (live) setMembers([]);
      }
    })();
    return () => { live = false; };
  }, []);

  /**
   * Merge what the operator confirmed on the screenshot into the row editor.
   *
   * MERGES, never replaces (CAPMARKETS-239). The partner sends one image per
   * account, and under a replace the second "Fill in" discarded the first
   * account's row — the operator wrote one account believing they wrote two.
   * `applyScreenshot` and `mergeProposals` (`lib/balanceEntry.js`) hold the
   * rules, and where they cannot tell which of two figures is right they keep
   * both on screen rather than pick:
   *
   * - A block filled in again updates the row it made — every block's row is
   *   found before any account is matched.
   * - A second figure for an account already here is a second row, whether it
   *   came from the same image or a later upload; the import refuses that
   *   batch, and the page names the account before Preview. The same figures
   *   read again stay one row, which says how many screenshots read it.
   * - A figure or account the operator changed after it was filled in is kept,
   *   and the row shows what the screenshot read. A block's figures are never
   *   filled into a row the operator has pointed at another account.
   * - A row the operator removed — with Remove, or by clearing it to blank — is
   *   not added back while its block still names the same account; the page
   *   says which were skipped. A write starts the importer over.
   * - A fill-in for a different member than the rows already filled in waits
   *   for the operator: replace those rows, keep both, or cancel. The question
   *   is worked out from the rows as they are whenever it is shown or answered,
   *   so Replace removes exactly the rows it lists. Keep both is remembered per
   *   pair of members, and while a question waits another member's fill-in is
   *   held rather than replacing it — and the page keeps saying so until that
   *   member is filled in, or a write.
   * - Rows match on the trimmed account ref, never the label, which is
   *   operator-editable (CAPMARKETS-133). A proposal with no ref is appended.
   * - A row with nothing in it — the seeded starter row — is dropped, so it
   *   cannot fail validation and block the write. Any row with content is kept.
   *
   * Every change goes through `setEditor` with an updater, so the decision is
   * made against the current rows however this callback is memoised.
   */
  const applyFromScreenshot = useCallback((incoming, source) => {
    setPreview(null);
    setServerError(null);
    setEditor((current) => applyScreenshot(current, incoming, source, blankRow));
  }, []);

  const resolveFillIn = useCallback((choice) => {
    if (choice !== "cancel") {
      setPreview(null);
      setServerError(null);
    }
    setEditor((current) => resolvePendingApply(current, choice, blankRow));
  }, []);

  const nameById = useMemo(() => new Map(members.map((m) => [m.id, m.name])), [members]);
  const memberLabel = (id) => nameById.get(id) || id;
  const duplicates = useMemo(() => duplicateAccountRefs(rows), [rows]);

  const withAsOf = useMemo(
    () => rows.map((r) => ({ ...r, asOf })),
    [rows, asOf],
  );

  const errors = useMemo(
    () => withAsOf.map((r) => validateRow(r)),
    [withAsOf],
  );
  const hasErrors = errors.some((e) => Object.keys(e).length > 0);

  /**
   * Any edit invalidates a preview. A preview that outlived the figures it
   * described would be the worst thing on this page — an operator confirming a
   * number they were shown for different input.
   */
  const update = useCallback((key, field, value) => {
    setPreview(null);
    setServerError(null);
    setEditor(withRows((rs) => rs.map((r) => (r.key === key ? { ...r, [field]: value } : r))));
  }, []);

  const submit = useCallback(
    async (dryRun) => {
      setBusy(true);
      setServerError(null);
      try {
        const csv = buildCsv(withAsOf, keyedBy);
        const form = new FormData();
        form.append(
          "file",
          new Blob([csv], { type: "text/csv" }),
          `balance-entry-${asOf}.csv`,
        );
        form.append("confidence", confidence);
        if (keyedBy === "display_name") {
          // The route refuses to key on a name by accident; this is the
          // explicit opt-in, and it makes the response carry the fork warning.
          form.append("ref_column", DISPLAY_NAME_REF_COLUMN);
        }
        form.append("dry_run", dryRun ? "true" : "false");
        const { data } = await api.post("/partner-statements/import", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (dryRun) {
          setPreview(data);
        } else {
          setPreview(null);
          setEditor(freshEditor());
          setImporterKey((k) => k + 1);
          toast.success(
            `Wrote ${data.accepted} snapshot(s).` +
              (data.already_present
                ? ` ${data.already_present} were already present and were not re-written.`
                : ""),
          );
        }
      } catch (e) {
        setPreview(null);
        setServerError(errorText(e, "The import failed and nothing was written."));
      } finally {
        setBusy(false);
      }
    },
    [withAsOf, asOf, confidence, keyedBy],
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Enter partner balances</h1>
          <p className="text-foreground-muted mt-1 max-w-3xl">
            Enter the figures from a partner console card. Equity is derived as
            balance + open trades profit — the card does not carry it directly.
            Nothing is written until you have previewed the effect on the
            member&apos;s dashboard.
          </p>
        </div>

        <StatementScreenshotImport key={importerKey} members={members} onApply={applyFromScreenshot} />

        {skipped.length > 0 && (
          <div
            data-testid="not-refilled"
            className="flex items-start gap-2 rounded-md border border-warning bg-background-subtle p-3 text-sm text-foreground"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <span>
              {skipped.length === 1 ? "A row you removed was" : `${skipped.length} rows you removed were`}{" "}
              not filled in again: <strong>{skipped.join(", ")}</strong>. Upload that screenshot
              again, or choose a different account for the block, to fill it in.
            </span>
          </div>
        )}

        {question && (
          <div
            data-testid="member-switch"
            className="flex items-start gap-3 rounded-md border border-warning bg-background-subtle p-4 text-sm text-foreground"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
            <div className="space-y-3">
              <p>
                <strong>Nothing has been filled in yet.</strong>{" "}
                {question.incoming.length === 1 ? "This row is" : `These ${question.incoming.length} rows are`} for{" "}
                <strong>{memberLabel(question.memberId)}</strong>
                {question.others.length ? (
                  <>
                    , but rows below were filled in from a screenshot for{" "}
                    {question.others.map((o, i) => (
                      <span key={o.memberId}>
                        {i > 0 ? ", " : ""}
                        <strong>{memberLabel(o.memberId)}</strong>
                        {` (${o.rows.map(namedRow).join(", ")})`}
                      </span>
                    ))}
                    . If those were filled in for the wrong member, replace them.
                  </>
                ) : (
                  ". No row from another member is left on the page, so nothing would be replaced."
                )}
              </p>
              <div className="flex flex-wrap gap-2">
                {question.others.length > 0 && (
                  <Button onClick={() => resolveFillIn("replace")} data-testid="member-switch-replace">
                    Replace those rows
                  </Button>
                )}
                <Button variant="outline" onClick={() => resolveFillIn("keep")} data-testid="member-switch-keep">
                  {question.others.length ? "Keep both" : "Fill in"}
                </Button>
                <Button variant="ghost" onClick={() => resolveFillIn("cancel")} data-testid="member-switch-cancel">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {held.length > 0 && (
          <div
            data-testid="fill-in-held"
            className="flex items-start gap-2 rounded-md border border-warning bg-background-subtle p-3 text-sm text-foreground"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <span>
              Nothing was filled in for{" "}
              {held.map((m, i) => (
                <span key={m}>
                  {i > 0 ? ", " : ""}
                  <strong>{memberLabel(m)}</strong>
                </span>
              ))}
              {question
                ? ". Answer the question above first, then fill those rows in again."
                : ", because a question was waiting. Fill those rows in again, uploading the screenshot again if it is no longer above."}
            </span>
          </div>
        )}

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-medium text-foreground">
                  Statement date <span className="text-danger">*</span>
                </span>
                <Input
                  type="date"
                  value={asOf}
                  data-testid="as-of"
                  onChange={(e) => {
                    setPreview(null);
                    setAsOf(e.target.value);
                  }}
                />
                <span className="text-xs text-foreground-soft">
                  The date the partner&apos;s figures are FROM. Never assumed to
                  be today — a stale figure dated by its entry time reads as
                  current.
                </span>
              </label>
              <label className="block">
                <span className="text-sm font-medium text-foreground">What is in the Account field?</span>
                <select
                  value={keyedBy}
                  data-testid="keyed-by"
                  onChange={(e) => { setPreview(null); setKeyedBy(e.target.value); }}
                  className="w-full rounded-md border border-border bg-background-elevated px-3 py-2 text-foreground"
                >
                  <option value="login">Account logins (e.g. 989761)</option>
                  <option value="display_name">Console display names (e.g. JC CAP 1)</option>
                </select>
                <span className="text-xs text-foreground-soft">
                  A display name is editable in the partner console, so renaming
                  the account forks its history here. The row records which you
                  used.
                </span>
              </label>
              <label className="block">
                <span className="text-sm font-medium text-foreground">How were these obtained?</span>
                <select
                  value={confidence}
                  data-testid="confidence"
                  onChange={(e) => setConfidence(e.target.value)}
                  className="w-full rounded-md border border-border bg-background-elevated px-3 py-2 text-foreground"
                >
                  {CONFIDENCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <span className="text-xs text-foreground-soft">
                  Travels with the figure to the member&apos;s dashboard.
                </span>
              </label>
            </div>
          </CardContent>
        </Card>

        {rows.map((row, index) => {
          const rowErrors = errors[index] || {};
          const equity = deriveEquity(row.balance, row.openTradesProfit);
          // A figure the operator changed after a screenshot filled it in is
          // kept on the next fill-in; say what the screenshot read, so the
          // difference is on screen rather than in someone's memory.
          const read = screenshotDisagreements(row);
          return (
            <Card key={row.key} data-testid="entry-row">
              <CardContent className="pt-6">
                {row.memberId && (
                  <p className="mb-3 text-xs text-foreground-soft" data-testid={`row-member-${index}`}>
                    Filled in from a screenshot for {memberLabel(row.memberId)}
                  </p>
                )}
                {screenshotReadings(row) > 1 && (
                  <p className="mb-3 text-xs text-foreground-soft" data-testid={`row-readings-${index}`}>
                    Read from {screenshotReadings(row)} screenshots, and no figure in them disagrees
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
                  <label className="block md:col-span-1">
                    <span className="text-sm font-medium text-foreground">Account</span>
                    <Input
                      value={row.accountRef}
                      data-testid={`account-${index}`}
                      placeholder="989761"
                      onChange={(e) => update(row.key, "accountRef", e.target.value)}
                    />
                    {rowErrors.accountRef && (
                      <span className="text-xs text-danger">{rowErrors.accountRef}</span>
                    )}
                    {"accountRef" in read && (
                      <span className="block text-xs text-warning" data-testid={`account-${index}-screenshot`}>
                        The screenshot was matched to {read.accountRef}; your entry is kept.
                      </span>
                    )}
                  </label>
                  <label className="block md:col-span-1">
                    <span className="text-sm font-medium text-foreground">Label (optional)</span>
                    <Input
                      value={row.accountLabel}
                      placeholder="JC CAP 1"
                      onChange={(e) => update(row.key, "accountLabel", e.target.value)}
                    />
                  </label>
                  <label className="block md:col-span-1">
                    <span className="text-sm font-medium text-foreground">Balance</span>
                    <Input
                      value={row.balance}
                      data-testid={`balance-${index}`}
                      placeholder="112,951.02"
                      onChange={(e) => update(row.key, "balance", e.target.value)}
                    />
                    {rowErrors.balance && (
                      <span className="text-xs text-danger">{rowErrors.balance}</span>
                    )}
                    {"balance" in read && (
                      <span className="block text-xs text-warning" data-testid={`balance-${index}-screenshot`}>
                        The screenshot read {screenshotFigure(read.balance)}; your entry is kept.
                      </span>
                    )}
                  </label>
                  <label className="block md:col-span-1">
                    <span className="text-sm font-medium text-foreground">Open trades profit</span>
                    <Input
                      value={row.openTradesProfit}
                      data-testid={`open-${index}`}
                      placeholder="-323.24"
                      onChange={(e) => update(row.key, "openTradesProfit", e.target.value)}
                    />
                    {rowErrors.openTradesProfit && (
                      <span className="text-xs text-danger">{rowErrors.openTradesProfit}</span>
                    )}
                    {"openTradesProfit" in read && (
                      <span className="block text-xs text-warning" data-testid={`open-${index}-screenshot`}>
                        The screenshot read {screenshotFigure(read.openTradesProfit)}; your entry is kept.
                      </span>
                    )}
                  </label>
                  <div className="md:col-span-1">
                    <span className="text-sm font-medium text-foreground flex items-center gap-1">
                      <Calculator className="h-3 w-3" /> Equity
                    </span>
                    <div className="mt-2 font-mono text-lg" data-testid={`equity-${index}`}>
                      {equity === null ? (
                        <span className="text-foreground-soft italic text-sm">
                          Needs both figures
                        </span>
                      ) : (
                        describeMoney(equity).text
                      )}
                    </div>
                    {rows.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-1"
                        onClick={() => {
                          setPreview(null);
                          setEditor((current) => removeRow(current, row.key));
                        }}
                      >
                        <Trash2 className="h-3 w-3 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {duplicates.length > 0 && (
          <div
            data-testid="duplicate-accounts"
            className="flex items-start gap-2 rounded-md border border-warning bg-background-subtle p-3 text-sm text-foreground"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <span>
              {duplicates.length === 1 ? "Account " : "Accounts "}
              <strong>{duplicates.join(", ")}</strong>
              {duplicates.length === 1 ? " is" : " are"} on more than one row. An account
              can have only one figure for a statement date, so the import will refuse
              this whole batch — nothing is written — until you remove the row that is
              wrong.
            </span>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={() => { setPreview(null); setEditor(withRows((rs) => [...rs, blankRow()])); }}
          >
            <Plus className="h-4 w-4 mr-1" /> Add another account
          </Button>
          <Button
            onClick={() => submit(true)}
            disabled={busy || hasErrors}
            data-testid="preview"
          >
            <Eye className="h-4 w-4 mr-1" /> Preview
          </Button>
          <Button
            onClick={() => submit(false)}
            disabled={busy || hasErrors || !preview}
            data-testid="commit"
            title={!preview ? "Preview first — you should see the member's figure before writing it" : undefined}
          >
            <Save className="h-4 w-4 mr-1" /> Write these balances
          </Button>
        </div>

        {serverError && (
          <Card className="border-danger">
            <CardContent className="pt-6 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-danger shrink-0" />
              <div>
                <p className="font-medium text-foreground">Nothing was written</p>
                <p className="text-foreground-muted text-sm" data-testid="server-error">{serverError}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {preview && (
          <Card data-testid="preview-panel">
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-foreground">What this will change</h2>
                <Badge variant="outline">Nothing written yet</Badge>
              </div>

              <p className="text-sm text-foreground-muted">
                {preview.would_accept} row(s) would be written
                {preview.already_present
                  ? `; ${preview.already_present} already present and would be skipped`
                  : ""}
                .
              </p>

              {/* A write starts the page over and clears the held note, so the last
                  screen before it says whose statement is not in it (CAPMARKETS-239,
                  final review of 0c3a3044). */}
              {held.length > 0 && (
                <p className="text-sm text-warning" data-testid="preview-held">
                  Not in this preview:{" "}
                  <strong>{held.map((m) => memberLabel(m)).join(", ")}</strong> — held back while a
                  question was waiting, so nothing was filled in. Writing starts the page over and
                  clears that note.
                </p>
              )}

              {preview.warning && (
                <p className="text-sm text-warning" data-testid="ref-warning">
                  {preview.warning}
                </p>
              )}

              {preview.unresolved?.length > 0 && (
                <p className="text-sm text-warning" data-testid="unresolved">
                  {preview.unresolved.length} account(s) are not mapped to any
                  member. Their figures will be stored and will appear in the
                  Partner Accounts queue — they will not reach a dashboard until
                  attached.
                </p>
              )}

              {(preview.projection || []).map((entry) => {
                const p = describeProjection(entry);
                return (
                  <div key={p.memberId} className="border border-border rounded-lg p-4" data-testid="projection">
                    {/* The name, not only the id. A card for a member nobody
                        meant to write to has to stand out (CAPMARKETS-239). */}
                    {nameById.get(p.memberId) && (
                      <p className="text-sm font-medium text-foreground" data-testid="projection-member">
                        {nameById.get(p.memberId)}
                      </p>
                    )}
                    <p className="text-xs text-foreground-soft font-mono">{p.memberId}</p>
                    <div className="flex flex-wrap gap-6 mt-2">
                      <div>
                        <p className="text-xs text-foreground-soft">Dashboard shows now</p>
                        <Money value={entry.before?.equity} />
                      </div>
                      <div>
                        <p className="text-xs text-foreground-soft">Will show</p>
                        <Money value={entry.after?.equity} />
                      </div>
                      <div>
                        <p className="text-xs text-foreground-soft">Change</p>
                        {p.delta === null ? (
                          <span className="text-foreground-soft italic">Unknown</span>
                        ) : (
                          <span className={`font-mono ${p.delta >= 0 ? "text-success" : "text-danger"}`}>
                            {p.delta >= 0 ? "+" : ""}{describeMoney(p.delta).text}
                          </span>
                        )}
                      </div>
                    </div>
                    {p.unchanged && (
                      <p className="text-xs text-warning mt-2" data-testid="unchanged-warning">
                        This member&apos;s total does not change. The figures
                        you entered are older than a statement already recorded
                        for this account, so the newer one still wins. Check the
                        statement date.
                      </p>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminBalanceEntryPage;
